import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Box, Button, GlobalStyles, IconButton} from '@mui/material';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import GCFLogo from '../../../assets/images/cpc/CPCIndexLogo-white.png';
import GalanoRegular from '../../../assets/fonts/cpc/GalanoGrotesque-Regular.otf';
import GalanoBold from '../../../assets/fonts/cpc/GalanoGrotesque-Bold.otf';
import {
    cpcGovLogoutService,
    clearCpcGovSession,
    getCpciCountriesService,
    getCpcGovUser,
    isCpcGovLoggedIn,
} from '../../../services/cpcGov.service';

const C = {
    navy: '#003C69',
    navyDeep: '#003D69',
    navyDark: '#002855',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: '#F5F8FB',
    muted: 'rgba(0,60,105,.72)',
    // The page background peeking around/behind every dashboard card — the
    // only genuinely "neutral" surface in this file, so it's the only color
    // here that needs to flip dark. The navy sidebar and its white-on-navy
    // text are already a fixed, self-contained brand block in both themes.
    pageBg: 'var(--cpc-surface-subtle, #F5F8FB)',
};

const SIDEBAR_W = 260;

const fontFaceStyles = {
    '@font-face': [
        {fontFamily: 'GalanoGrotesque', fontWeight: 400, src: `url(${GalanoRegular}) format("opentype")`, fontDisplay: 'swap'},
        {fontFamily: 'GalanoGrotesque', fontWeight: 700, src: `url(${GalanoBold}) format("opentype")`, fontDisplay: 'swap'},
    ],
};

const NAV_ITEMS = [
    {key: 'about', label: 'About', path: '/dq_pro/cpc-government-dashboard'},
    {key: 'overview', label: 'National Overview', path: '/dq_pro/cpc-government-dashboard/overview'},
    {key: 'explorer', label: 'Checklist Explorer', path: '/dq_pro/cpc-government-dashboard/explorer'},
    {key: 'progress', label: 'Review Progress', path: '/dq_pro/cpc-government-dashboard/progress'},
];

const OVERVIEW_SUBITEMS = [
    {label: 'Executive Summary', path: '/dq_pro/cpc-government-dashboard/overview/executive-summary'},
    {label: 'Stakeholder Scores', path: '/dq_pro/cpc-government-dashboard/overview/stakeholder-scores'},
    {label: 'Target Scores', path: '/dq_pro/cpc-government-dashboard/overview/target-scores'},
];

const navBtnSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    justifyContent: 'flex-start',
    width: '100%',
    border: 'none',
    borderLeft: '3px solid transparent',
    background: 'transparent',
    color: 'rgba(255,255,255,.72)',
    fontFamily: 'inherit',
    fontSize: 14.5,
    fontWeight: 600,
    textTransform: 'none',
    textAlign: 'left',
    px: 1.75,
    py: 1.375,
    borderRadius: '4px',
    transition: 'background .15s ease, color .15s ease',
    '&:hover': {background: 'rgba(255,255,255,.06)', color: C.white},
};

const navBtnActiveSx = {
    background: 'rgba(0,150,200,.16)',
    borderLeftColor: C.cyan,
    color: C.white,
};

const subNavBtnSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    border: 'none',
    borderLeft: '2px solid rgba(255,255,255,.14)',
    background: 'transparent',
    color: 'rgba(255,255,255,.64)',
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: 600,
    textTransform: 'none',
    textAlign: 'left',
    px: 1.5,
    py: 1.125,
    borderRadius: 0,
    transition: 'background .15s ease, color .15s ease, border-color .15s ease',
    '&:hover': {background: 'rgba(255,255,255,.06)', color: C.white},
};

const subNavBtnActiveSx = {
    borderLeftColor: C.cyan,
    color: C.white,
    fontWeight: 700,
};

/**
 * Persistent dashboard shell — left sidebar navigation linking About,
 * National Overview, Checklist Explorer, and Review Progress, plus the
 * country/user context strip (mirrors the mockup's top-bar content, moved
 * into a side menu per the dashboard-layout requirement).
 */
const DashboardShell = ({active, children}) => {
    const history = useHistory();
    const location = useLocation();
    const [country, setCountry] = useState('Country X');
    const [signingOut, setSigningOut] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const goTo = (path) => {
        setMobileMenuOpen(false);
        history.push(path);
    };
    // Resolves a raw country_code (the signed-in case) to its full UN name.
    // Signed out, `country` already holds the full name chosen on the User
    // Information screen, so a miss here just falls through to that as-is.
    const [countries, setCountries] = useState([]);

    const authed = isCpcGovLoggedIn();

    /**
     * The dashboard requires a session. Without this guard an EXPIRED token left
     * the reviewer looking at a working-looking dashboard full of sample data:
     * invokeApi's 401 handler clears storage and reloads the SAME url, so
     * /explorer simply re-rendered in signed-out mode showing "Country X" and ten
     * mock indicators. Someone could easily have believed those were their
     * country's real figures.
     *
     * `replace`, not `push`, so Back does not return to a page they cannot load.
     */
    useEffect(() => {
        if (!authed) history.replace('/dq_pro/cpc-government-dashboard/login');
    }, [authed, history]);

    useEffect(() => {
        // Signed in: the country is the account's fixed scope, so it wins over
        // anything left in sessionStorage from the pre-login review flow.
        if (authed) {
            const user = getCpcGovUser();
            if (user && user.country_code) {
                setCountry(user.country_code);
                return;
            }
        }
        try {
            const raw = sessionStorage.getItem('cpcGovernmentUserInfo');
            if (raw) {
                const ui = JSON.parse(raw);
                if (ui.country) setCountry(ui.country);
            }
        } catch (_) {
            /* ignore */
        }
    }, [authed]);

    useEffect(() => {
        let alive = true;
        getCpciCountriesService()
            .then((res) => {
                const list = res?.data?.countries;
                if (alive && Array.isArray(list)) setCountries(list);
            })
            .catch(() => { /* best effort — the code still displays, just unresolved */ });
        return () => { alive = false; };
    }, []);

    const countryName = useMemo(() => {
        const hit = countries.find((c) => c.country_code === country);
        return hit ? hit.country_name : country;
    }, [countries, country]);

    const signOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        // The service clears the local session even if the server call fails, so
        // this can never leave someone in a signed-in-looking state.
        await cpcGovLogoutService();
        history.push('/dq_pro/cpc-government-dashboard/login');
    };

    // Redirect pending — render nothing rather than flash sample data at someone
    // whose session has just expired.
    if (!authed) return null;

    return (
        <Box
            className="cpc-gov-scope"
            sx={{
                display: 'flex',
                minHeight: '100vh',
                fontFamily: 'GalanoGrotesque, "Helvetica Neue", Helvetica, Arial, sans-serif',
                background: C.pageBg,
                WebkitFontSmoothing: 'antialiased',
                '@media (max-width: 900px)': {flexDirection: 'column'},
            }}
        >
            <GlobalStyles styles={fontFaceStyles}/>
            <Box
                component="aside"
                sx={{
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    flex: `0 0 ${SIDEBAR_W}px`,
                    width: SIDEBAR_W,
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    background: C.navyDeep,
                    '@media (max-width: 900px)': {
                        position: 'static',
                        flex: '0 0 auto',
                        width: '100%',
                        height: 'auto',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        padding: '0 24px 24px',
                        borderBottom: '1px solid rgba(255,255,255,.1)',
                        '@media (max-width: 900px)': {padding: '14px 18px'},
                    }}
                >
                    <Box
                        component="img"
                        src={GCFLogo}
                        alt=""
                        sx={{height: 85, width: 'auto', filter: 'brightness(0) invert(1)', '@media (max-width: 900px)': {height: 44}}}
                    />
                    <IconButton
                        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        onClick={() => setMobileMenuOpen((o) => !o)}
                        sx={{
                            display: 'none',
                            color: C.white,
                            '@media (max-width: 900px)': {display: 'inline-flex'},
                        }}
                    >
                        {mobileMenuOpen ? <CloseIcon/> : <MenuIcon/>}
                    </IconButton>
                </Box>
                <Box
                    component="nav"
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        padding: '16px 14px',
                        overflowY: 'auto',
                        '@media (max-width: 900px)': {display: mobileMenuOpen ? 'flex' : 'none'},
                    }}
                >
                    {NAV_ITEMS.map((item) => (
                        <React.Fragment key={item.key}>
                            <Button
                                type="button"
                                disableRipple
                                onClick={() => goTo(item.path)}
                                sx={{...navBtnSx, ...(active === item.key ? navBtnActiveSx : {})}}
                            >
                                {item.label}
                            </Button>
                            {item.key === 'overview' && active === 'overview' && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.25,
                                        padding: '4px 0 6px 20px',
                                    }}
                                >
                                    {OVERVIEW_SUBITEMS.map((sub) => (
                                        <Button
                                            key={sub.path}
                                            type="button"
                                            disableRipple
                                            onClick={() => goTo(sub.path)}
                                            sx={{
                                                ...subNavBtnSx,
                                                ...(location.pathname === sub.path ? subNavBtnActiveSx : {}),
                                            }}
                                        >
                                            {sub.label}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                <Box
                    sx={{
                        padding: '16px 20px 22px',
                        borderTop: '1px solid rgba(255,255,255,.1)',
                        '@media (max-width: 900px)': {display: mobileMenuOpen ? 'block' : 'none'},
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1.75,
                            padding: '10px 12px',
                            background: 'rgba(255,255,255,.05)',
                            borderRadius: '10px',
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{width: 8, height: 8, mt: '5px', borderRadius: '50%', background: '#5c9469', flex: 'none'}}
                        />
                        <Box sx={{minWidth: 0}}>
                            <Box
                                component="span"
                                title={countryName}
                                sx={{
                                    display: 'block',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: C.white,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {countryName}
                            </Box>
                            <Box component="span" sx={{display: 'block', fontSize: 11.5, color: 'rgba(255,255,255,.6)', mt: '1px'}}>
                                2025–2026 Baseline
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                        <Button
                            type="button"
                            title="Add or edit your User Information"
                            startIcon={<PersonOutlineIcon fontSize="small"/>}
                            onClick={() => goTo('/dq_pro/cpc-government-dashboard/user-info')}
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                border: '1px solid rgba(255,255,255,.22)',
                                background: 'transparent',
                                color: C.white,
                                fontFamily: 'inherit',
                                fontSize: 13,
                                fontWeight: 600,
                                textTransform: 'none',
                                padding: '9px 14px',
                                borderRadius: '10px',
                                transition: 'background .15s ease, border-color .15s ease',
                                '&:hover': {background: 'rgba(255,255,255,.08)'},
                            }}
                        >
                            Edit details
                        </Button>
                        {/* Only when there is a real session to end. Signed out, the
                            dashboard is still reachable for review, and a sign-out
                            button there would do nothing. */}
                        {authed && (
                            <Button
                                type="button"
                                onClick={signOut}
                                disabled={signingOut}
                                title="End this session"
                                startIcon={<PowerSettingsNewIcon fontSize="small"/>}
                                sx={{
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    border: '1px solid rgba(255,255,255,.22)',
                                    background: 'transparent',
                                    color: C.white,
                                    fontFamily: 'inherit',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    padding: '9px 14px',
                                    borderRadius: '10px',
                                    transition: 'background .15s ease, border-color .15s ease',
                                    '&:hover': {background: 'rgba(192,85,58,.16)', borderColor: 'rgba(192,85,58,.5)'},
                                    '&.Mui-disabled': {color: 'rgba(255,255,255,.5)'},
                                }}
                            >
                                {signingOut ? 'Signing out…' : 'Sign out'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box component="main" sx={{flex: 1, minWidth: 0}}>{children}</Box>
        </Box>
    );
};

export default DashboardShell;
